
// ---------------------------
// Config
// ---------------------------
export interface OffsetOptions {
	offset: number; // desired offset distance (positive = outward)
	miterLimit?: number; // prevent extremely long miters (default 4.0)
	eps?: number; // numeric tolerance for equality (default 1e-6)
}

// ---------------------------
// Math helpers
// ---------------------------
function add(a: number[], b: number[]) { return [a[0] + b[0], a[1] + b[1]]; }
function sub(a: number[], b: number[]) { return [a[0] - b[0], a[1] - b[1]]; }
function mul(a: number[], s: number) { return [a[0] * s, a[1] * s]; }
function dot(a: number[], b: number[]) { return a[0] * b[0] + a[1] * b[1]; }
function len(a: number[]) { return Math.hypot(a[0], a[1]); }
function normalize(a: number[]) {
	const L = len(a);
	return L === 0 ? [0, 0] : [a[0] / L, a[1] / L];
}
function perpLeft(a: number[]) { return [-a[1], a[0]]; } // left-hand perpendicular
function midpoint(a: number[], b: number[]) { return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]; }

function samePoint(a: number[], b: number[], eps = 1e-6) {
	return Math.abs(a[0] - b[0]) <= eps && Math.abs(a[1] - b[1]) <= eps;
}

// ---------------------------
// Build continuous chains (preserve gaps)
// Input: many edges (pairs [[x1,y1],[x2,y2]])
// Output: array of chains; each chain is an ordered array of coords
// ---------------------------
function buildChains(edges: number[][][], eps = 1e-6): number[][][] {
	const chains: number[][][] = [];

	for (const e of edges) {
		if (!e || e.length < 2) continue;
		const A = e[0];
		const B = e[1];

		chains.push([A, B]);
	}

	let merged = true;
	while (merged) {
		merged = false;

		for (let i = 0; i < chains.length; i++) {
			for (let j = i + 1; j < chains.length; j++) {
				const c1 = chains[i];
				const c2 = chains[j];

				const c1f = c1[0];
				const c1l = c1[c1.length - 1];
				const c2f = c2[0];
				const c2l = c2[c2.length - 1];

				if (samePoint(c1l, c2f, eps)) {
					chains[i] = [...c1, ...c2.slice(1)];
					chains.splice(j, 1);
					merged = true;
					break;
				}
				if (samePoint(c1l, c2l, eps)) {
					chains[i] = [...c1, ...c2.slice(0, -1).reverse()];
					chains.splice(j, 1);
					merged = true;
					break;
				}
				if (samePoint(c1f, c2l, eps)) {
					chains[i] = [...c2, ...c1.slice(1)];
					chains.splice(j, 1);
					merged = true;
					break;
				}
				if (samePoint(c1f, c2f, eps)) {
					chains[i] = [...c2.reverse(), ...c1.slice(1)];
					chains.splice(j, 1);
					merged = true;
					break;
				}
			}
			if (merged) break;
		}
	}

	return chains;
}

// ---------------------------
// Mitered offset for a chain of points (open polyline)
// Returns offset polyline (same number of vertices for interior vertices; ends handled separately)
// ---------------------------
function offsetChainMiter(points: number[][], options: OffsetOptions): number[][] {
	const { offset, miterLimit = 4.0, eps = 1e-6 } = options;
	if (!points || points.length < 2) return [];

	const n = points.length;

	// Compute centroid (used to pick outward direction)
	let cx = 0, cy = 0;
	for (const p of points) { cx += p[0]; cy += p[1]; }
	cx /= n; cy /= n;
	const centroid = [cx, cy];

	// Precompute edge directions and outward normals for each segment (i -> i+1)
	const segDirs: number[][] = [];
	const segNormalsOut: number[][] = []; // unit normals pointing outward (away from centroid)
	for (let i = 0; i < n - 1; i++) {
		const A = points[i];
		const B = points[i + 1];
		const v = sub(B, A);
		const uv = normalize(v);
		segDirs.push(uv);
		// left normal (perpLeft) points to the left of the directed edge.
		let n1 = perpLeft(uv);
		// choose direction such that normal points away from centroid (outward)
		const mid = midpoint(A, B);
		const toMid = sub(mid, centroid);
		if (dot(n1, toMid) < 0) {
			// flip
			n1 = mul(n1, 1);
		}
		segNormalsOut.push(n1);
	}

	// For each vertex compute offset vertex
	const outVerts: number[][] = new Array(n).fill(null);

	for (let i = 0; i < n; i++) {
		// Endpoints of an open chain: simply shift along adjacent segment normal
		if (i === 0) {
			// first point uses segNormalsOut[0]
			const n0 = segNormalsOut[0];
			outVerts[i] = add(points[i], mul(n0, offset));
			continue;
		}
		if (i === n - 1) {
			const nPrev = segNormalsOut[n - 2];
			outVerts[i] = add(points[i], mul(nPrev, offset));
			continue;
		}

		// interior vertex: use bisector of the two adjacent segment normals
		const nPrev = segNormalsOut[i - 1];
		const nNext = segNormalsOut[i];

		// sum of normals
		const sum = add(nPrev, nNext);
		const sumLen = len(sum);

		if (sumLen < eps) {
			// Normals nearly opposite => edges are roughly collinear (straight or 180deg turn).
			// Fallback: average offset of the two normals (which are opposite in weird cases) or use nPrev.
			// Use nPrev (or nNext), scaled by offset.
			outVerts[i] = add(points[i], mul(nPrev, offset));
			continue;
		}

		// unit bisector (not geometric bisector of edges but bisector of normals)
		const bis = normalize(sum);

		// miterLength = offset / dot(bis, nPrev)
		const denom = dot(bis, nPrev);
		if (Math.abs(denom) < 1e-12) {
			// numerical issue; fallback to simple offset
			outVerts[i] = add(points[i], mul(nPrev, offset));
			continue;
		}

		const miterLength = offset / denom;

		// clamp miter length to avoid extreme spikes (miterLimit * offset)
		const maxMiter = Math.max(1, miterLimit) * Math.abs(offset);
		if (Math.abs(miterLength) > maxMiter) {
			// bevel fallback: use average of two offset points (prevents spikes)
			const p1 = add(points[i], mul(nPrev, offset));
			const p2 = add(points[i], mul(nNext, offset));
			outVerts[i] = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
		} else {
			outVerts[i] = add(points[i], mul(bis, miterLength));
		}
	}

	return outVerts;
}

// ---------------------------
// Build final gutter segments for a list of facet gutter edges
// Input: array of facet-gutter objects: each has coords [[x1,y1],[x2,y2]]
// Output: an array of GutterOut segments (id + coords) usable directly in Konva
// ---------------------------
function buildAllGuttersMitered(
	facetGutters: GutterLine[],
	options: OffsetOptions = {
		offset: 1,
		miterLimit: 1,
		eps: 1
	}
) {
	// collect all edges as simple pairs
	const allEdges: number[][][] = [];
	for (const g of facetGutters) {
		if (!g?.coords) continue;
		// some inputs may have entire edge polyline but you said each gutter is single edge per facet;
		// if there are multi-point polylines, split into segment pairs
		if (g.coords.length === 2) {
			allEdges.push([g.coords[0], g.coords[1]]);
		} else if (g.coords.length > 2) {
			for (let i = 0; i < g.coords.length - 1; i++) {
				allEdges.push([g.coords[i], g.coords[i + 1]]);
			}
		}
	}

	// Build chains preserving gaps
	const chains = buildChains(allEdges, options.eps ?? 1e-6);

	const gutterLinesResult: Array<{coords: number[][]}> = [];

	for (let ci = 0; ci < chains.length; ci++) {
		const chain = chains[ci];
		// If chain length < 2 skip
		if (!chain || chain.length < 2) continue;

		// offset chain with miter algorithm
		const offsetPoly = offsetChainMiter(chain, options);

		// Convert offsetPoly vertices into segments (consecutive pairs)
		for (let i = 0; i < offsetPoly.length - 1; i++) {
			const A = offsetPoly[i];
			const B = offsetPoly[i + 1];
			gutterLinesResult.push({
				coords: [[A[0], A[1]], [B[0], B[1]]]
			});
		}
	}

	return gutterLinesResult;
}

export {
	buildAllGuttersMitered
};